// src/components/authentication/AuthContext.jsx
import { createContext, useContext, useState, useEffect } from 'react';
import { API_URL } from '../../config';
import axios from 'axios';

const AuthContext = createContext({
  user:           null,
  authenticated:  false,
  loading:        false,
  setUser:        () => {},
});

export const AuthProvider = ({ children, initialUser = null }) => {
  const isServer = typeof window === 'undefined';

  /*
     ──────────────────────────
     1) Normalize initialUser
     ──────────────────────────
*/
  const initial =
    initialUser && initialUser.authenticated === false
      ? null
      : initialUser?.authenticated
      ? initialUser
      : null;

  const [user, setUser]             = useState(initial);
  const [loading, setLoading]       = useState(!initial);   // no initial → show spinner
  const authenticated = !!user;

  /*
     ──────────────────────────
     2) Client: soft /user request
     ──────────────────────────
*/
  useEffect(() => {
    if (isServer || initial) return;            // SSR already provided data, or this is the server

    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/user`, { withCredentials:true });

        if (data?.authenticated)        setUser(data);      // logged in
        else                            setUser(null);      // guest
      } catch (e) {
        console.error('GET /user failed:', e);
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [isServer, initial]);

  // Automatically set user_id in GA when the user loads
  useEffect(() => {
    if (user?.user_id && window.setGAUserId) {
      console.log('🔧 Setting GA user_id:', user.user_id);
      window.setGAUserId(user.user_id);
    } else if (!user && window.setGAUserId) {
      console.log('🔧 Clearing GA user_id');
      window.setGAUserId(null);
    }
  }, [user]);

  // Check the GA user_id cookie on page load
  useEffect(() => {
    const checkGAUserIdCookie = () => {
      const cookies = document.cookie.split(';');
      const gaUserIdCookie = cookies.find(cookie => 
        cookie.trim().startsWith('ga_user_id=')
      );
      
      if (gaUserIdCookie && window.setGAUserId) {
        const userId = gaUserIdCookie.split('=')[1];
        if (userId && userId !== 'null') {
          window.setGAUserId(userId);
        }
      }
    };

    // Check after a short delay so gtag can load
    const timer = setTimeout(checkGAUserIdCookie, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <AuthContext.Provider value={{ user, authenticated, loading, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
